import React, { lazy, Suspense, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { hot } from 'react-hot-loader/root';
import { useQuery } from '@apollo/react-hooks';

import Carousel, { Slide } from '../../library/carousel/carousel';
import Link from '../../library/link/link';
import Page, { Content, Section } from '../../library/page/page';
import CmsFilteredPromotions from '../../components/cms-filtered-promotions/cms-filtered-promotions';
import CmsHtmlContent from '../../components/cms-html-content/cms-html-content';
import CmsPromotionCarousel from '../../components/cms-promotion-carousel/cms-promotion-carousel';
import CmsSections from '../../components/cms-sections/cms-sections';
import FitmentSmartLoader from '../../components/fitment-smart-loader/fitment-smart-loader';
import CmsArticleList from './components/cms-article-list/cms-article-list';
import CmsFeaturedArticleList from './components/cms-featured-article-list/cms-featured-article-list';
import CmsShoppingCategories from './components/cms-shopping-categories/cms-shopping-categories';
import CmsShoppingGuide from './components/cms-shopping-guide/cms-shopping-guide';
import DefaultLayout from '../../layouts/default-layout/default-layout';
import { ORGANIZATION_LD, WEBSITE_LD } from './home-page-constants';

import { GET_HOME_PAGE } from './graphql/home-page-queries';

import { useDataLayer, useFitment } from '../../shared/hooks/hooks';

import { trackAction } from '../../shared/utils/analytics/analytics';
import { getCmsSiteId, metaTags } from '../../shared/utils/page/page';
import { isArrayWithLength } from '../../shared/utils/validators/validators';

import {
  PAGE_NAME,
  PAGE_SECTION_NAME,
  PAGE_SUBCATEGORY,
  PAGE_TYPE,
  PROMO_CREATIVE,
  PROMO_NAME,
  PROMO_POSITION
} from '../../shared/constants/data-layer';
import { SUGGESTED_SELLING_CONTEXTS } from '../../shared/constants/suggested-selling';

import './home-page.scss';

const SuggestedSellingPageSection = lazy(
  () =>
    import(
      '../../components/suggested-selling-page-section/suggested-selling-page-section'
    )
);

const cmsComponentMap = {
  CmsArticleListComponent: CmsArticleList,
  CmsFeaturedArticlesListComponent: CmsFeaturedArticleList,
  CmsFilteredPromotionsComponent: CmsFilteredPromotions,
  CmsHtmlComponent: CmsHtmlContent,
  CmsPromotionCarouselSectionComponent: CmsPromotionCarousel,
  CmsShoppingCategoriesComponent: CmsShoppingCategories,
  CmsShoppingGuideComponent: CmsShoppingGuide
};

function HomePage() {
  const { selectedFitment } = useFitment();
  const { updateDataLayer } = useDataLayer({
    shouldResetPageOnFitmentChange: true
  });
  const orgLDRef = useRef(JSON.stringify(ORGANIZATION_LD));
  const websiteLDRef = useRef(JSON.stringify(WEBSITE_LD));

  const { data, error } = useQuery(GET_HOME_PAGE, {
    variables: {
      id: 'homepage',
      siteId: getCmsSiteId()
    }
  });

  const { cms } = data || {};

  const { page: cmsPage } = cms || {};
  const showFitment = true;
  const {
    contentSlots = [],
    documentTitle,
    metaTags: metaData
  } = cmsPage || {};

  const [bannerSection, ...cmsSections] = contentSlots;
  const bannerComponent = bannerSection?.cmsComponents[0];
  const mainHero = bannerComponent?.slides?.[0] || {};
  const autoSlideInterval = bannerComponent?.slideInterval
    ? bannerComponent.slideInterval * 1000
    : null;
  const cmsPageId = 'home';

  function getHeroImgAttrs(hero) {
    const img = hero?.image || {};
    const imgUrl = img.url || '';

    return {
      alt: img.altText,
      sizes: `
      (-webkit-min-device-pixel-ratio: 2) 50vw,
      (min-resolution: 192dpi) 50vw,
      (-webkit-min-device-pixel-ratio: 3) 33.33vw,
      (min-resolution: 288dpi) 33.33vw
      `,
      src: `${imgUrl}?$h256$`,
      srcset: `
        ${imgUrl}?$h450$ 1200w,
        ${imgUrl}?$h400$ 1024w,
        ${imgUrl}?$h340$ 768w,
        ${imgUrl}?$h256$ 600w
      `
    };
  }

  /*
   * This function is ONLY used to stop Lighthouse (v8.0.0) from
   * complaining about the image width & height attrs not being set
   */
  function setImageSizeAttrs(e) {
    const img = e.target;

    img.setAttribute('width', img.width);
    img.setAttribute('height', img.height);
  }

  function trackHeroClick(hero, pos) {
    const { image } = hero || {};

    trackAction('promotion_select_home_hero', {
      [PROMO_CREATIVE]: image?.url,
      [PROMO_NAME]: image?.altText || '',
      [PROMO_POSITION]: pos
    });
  }

  const mainHeroImgAttrs = getHeroImgAttrs(mainHero);

  useEffect(() => {
    if (isArrayWithLength(contentSlots)) {
      updateDataLayer({
        [PAGE_NAME]: cmsPageId,
        [PAGE_SECTION_NAME]: documentTitle,
        [PAGE_SUBCATEGORY]: cmsPageId,
        [PAGE_TYPE]: cmsPageId
      });
    }
  }, [contentSlots, documentTitle, updateDataLayer]);

  useEffect(() => {
    function cmsContentTracking(e) {
      const linkText = e?.target?.innerText?.trim();

      if (linkText === 'use our tire guide') {
        trackAction('treadwell_use_tire_guide_click');
      } else if (linkText === 'get started') {
        trackAction('treadwell_touchless_get_started_click');
      }
    }

    document.body.addEventListener('click', cmsContentTracking);

    return () => {
      document.body.removeEventListener('click', cmsContentTracking);
    };
  }, []);

  if (error && !window.isPreRendering) {
    throw error;
  }

  return (
    <DefaultLayout>
      <Page>
        {!window.isPreRendering && (
          <Helmet>
            {documentTitle && <title>{documentTitle}</title>}
            {metaTags(metaData)}
            <link
              href={`${window.location.protocol}//${window.location.host}`}
              rel="canonical"
            />
            <script type="application/ld+json">{orgLDRef.current}</script>
            <script type="application/ld+json">{websiteLDRef.current}</script>
          </Helmet>
        )}

        {window.isPreRendering && mainHero && (
          <Helmet>
            <link
              as="image"
              href={mainHeroImgAttrs.src}
              imageSizes={mainHeroImgAttrs.sizes}
              imageSrcSet={mainHeroImgAttrs.srcset}
              rel="preload"
            />
          </Helmet>
        )}

        <div styleName="home-hero-container">
          {isArrayWithLength(bannerComponent?.slides) && (
            <>
              {bannerComponent.slides.length === 1 && mainHero ? (
                <Link
                  onClick={() => trackHeroClick(mainHero, 1)}
                  styleName="hero-image"
                  to={mainHero.link?.href}
                >
                  <img
                    alt={mainHeroImgAttrs.alt}
                    onLoad={setImageSizeAttrs}
                    sizes={mainHeroImgAttrs.sizes}
                    src={mainHeroImgAttrs.src}
                    srcSet={mainHeroImgAttrs.srcset}
                  />
                </Link>
              ) : (
                <Carousel
                  autoSlideInterval={autoSlideInterval}
                  hasFullWidthSlides
                >
                  {bannerComponent.slides.map((hero, idx) => {
                    const heroImgAttrs = getHeroImgAttrs(hero);

                    return (
                      <Slide key={`hero-${idx}`}>
                        <Link
                          onClick={() => trackHeroClick(hero, idx + 1)}
                          styleName="hero-image"
                          to={hero?.link?.href}
                        >
                          <img
                            alt={heroImgAttrs.alt}
                            onLoad={setImageSizeAttrs}
                            sizes={heroImgAttrs.sizes}
                            src={heroImgAttrs.src}
                            srcSet={heroImgAttrs.srcset}
                          />
                        </Link>
                      </Slide>
                    );
                  })}
                </Carousel>
              )}
            </>
          )}
        </div>
        <Content isFullWidth>
          {showFitment && (
            <Section isFullWidth styleName="fitment-section">
              <div styleName="fitment-container">
                <FitmentSmartLoader
                  onAddVehicleClicked={() => {
                    trackAction('vehicle_page_multiple_add_vehicle');
                  }}
                  onShopProducts={() => {
                    trackAction('vehicle_page_new_vehicle_add');
                  }}
                />
              </div>
            </Section>
          )}

          <CmsSections
            componentMap={cmsComponentMap}
            sectionHeight={sectionIndex => (sectionIndex === 0 ? 100 : 400)}
            sections={cmsSections}
            styleName="cms-sections"
          />

          {selectedFitment && (
            <Suspense fallback={<div />}>
              <SuggestedSellingPageSection
                pageContext={SUGGESTED_SELLING_CONTEXTS.HOMEPAGE}
              />
            </Suspense>
          )}
        </Content>
      </Page>
    </DefaultLayout>
  );
}

export default hot(HomePage);
